import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';

import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import mediaService from '../../services/mediaService';
import circlesService from '../../services/circlesService';


const Signup = () => {
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoginMode, setIsLoginMode] = useState(!!location.state?.openLogin);
  const [step, setStep] = useState(1);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [activeCircle, setActiveCircle] = useState('all');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    title: '',
    bio: '',
    picture: null,
    selectedCircles: []
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [circles, setCircles] = useState([]);

  useEffect(() => {
    const fetchCircles = async () => {
      try {
        const { data } = await circlesService.getCircles();
        // Filter out subcategories (circles with parents) for the signup selection
        const mainCircles = data.filter(c => !c.parentId).map(c => ({
          id: c.slug,
          name: c.name,
          icon: c.icon,
          color: c.color,
          subtext: c.subtext,
          hasChildren: c.hasChildren
        }));
        setCircles(mainCircles);
      } catch (error) {
        console.error('Failed to fetch circles:', error);
      }
    };
    fetchCircles();
  }, []);

  const handleFilterChange = (circleId, e) => {
    if (e) {
      e?.preventDefault();
    }
    if (activeCircle === circleId) {
      setActiveCircle('all');
    } else {
      setActiveCircle(circleId);
    }
    navigate('/');
  };

  // Arabic-only regex: Arabic letters, diacritics, and spaces
  const arabicNameRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s]+$/;

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    // Real-time Arabic validation for name field
    if (name === 'name' && value.trim() && !arabicNameRegex.test(value.trim())) {
      setErrors((prev) => ({ ...prev, name: 'يرجى إدخال الاسم بالحروف العربية فقط' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, picture: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleCircleToggle = (circleId) => {
    setFormData((prev) => ({
      ...prev,
      selectedCircles: prev?.selectedCircles?.includes(circleId) ?
        prev?.selectedCircles?.filter((id) => id !== circleId) :
        [...prev?.selectedCircles, circleId]
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});

    // Login Flow
    if (isLoginMode) {
      if (!formData.email || !formData.password) {
        setErrors({ form: 'يرجى إدخال البريد الإلكتروني وكلمة المرور' });
        return;
      }
      setLoading(true);
      const { data, error } = await signIn(formData.email, formData.password);
      setLoading(false);

      if (error) {
        setErrors({
          form: error.message,
          requiresVerification: error.requiresVerification,
          email: formData.email,
        });
      } else {
        navigate(location.state?.from || '/homepage');
      }
      return;
    }

    // Signup Flow
    // Step 1: Validate, check email availability, then move to Step 2
    if (step === 1) {
      const newErrors = {};
      if (!formData?.name) {
        newErrors.name = 'يرجى إدخال الاسم';
      } else if (!arabicNameRegex.test(formData.name.trim())) {
        newErrors.name = 'يرجى إدخال الاسم بالحروف العربية فقط';
      }
      if (!formData?.email) newErrors.email = 'يرجى إدخال البريد الإلكتروني';
      if (!formData?.password) newErrors.password = 'يرجى إدخال كلمة المرور';

      if (Object.keys(newErrors)?.length > 0) {
        setErrors(newErrors);
        return;
      }

      // Check if email is already registered before proceeding to circles selection
      setLoading(true);
      try {
        const { data } = await authService.checkEmail(formData.email.trim());
        if (data?.available === false) {
          setErrors({ email: data?.message || 'البريد الإلكتروني مسجل مسبقاً' });
          setLoading(false);
          return;
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'فشل التحقق من البريد الإلكتروني. يرجى المحاولة مرة أخرى.';
        setErrors({ email: msg });
        setLoading(false);
        return;
      }
      setLoading(false);
      setStep(2);
      return;
    }

    // Final Step (Member Step 2)
    setLoading(true);

    // Upload Avatar if exists
    let avatarUrl = null;
    if (formData.picture) {
      try {
        const { data } = await mediaService.uploadAvatar(formData.picture);
        avatarUrl = data.url || data; // Adjust based on actual API response
      } catch (err) {
        console.error('Avatar upload failed', err);
        // Continue without avatar or handle error
      }
    }

    const { data, error } = await signUp({
      fullName: formData.name,
      email: formData.email,
      password: formData.password,
      title: formData.title,
      bio: formData.bio,
      avatarUrl: avatarUrl || undefined,
      selectedCircleSlugs: formData.selectedCircles
    });

    setLoading(false);

    if (error) {
      setErrors({ form: error.message });
    } else if (data?.requiresVerification) {
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    } else {
      navigate('/homepage');
    }
  };

  const handleBackClick = () => {
    if (step === 2) {
      setStep(1);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header />
      {/* Mobile Header Bar */}
      <div className="flex lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#F9F7F2]/95 backdrop-blur-sm z-50 px-4 justify-between items-center">
        <div className="flex items-center gap-2">
          <Image
            src="/assets/images/header_logo-1769143036795.png"
            alt="شعار دوائر"
            className="h-8 object-contain"
            style={{ filter: 'brightness(0) saturate(100%) invert(61%) sepia(32%) saturate(567%) hue-rotate(358deg) brightness(92%) contrast(87%)' }} />
          <div className="flex flex-col justify-center">
            <h1
              className="text-xl font-bold leading-tight"
              style={{ fontFamily: 'Aref Ruqaa, serif', color: '#C5A059' }}>
              دوائر
            </h1>
            <p
              className="text-[9px] leading-tight whitespace-nowrap"
              style={{ color: '#5A5A5A' }}>
              نغرّد داخل السِرب؛ آملين أن تنبُتَ للجميع أجنحة.
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsMobileDrawerOpen(true)}
          className="flex items-center justify-center w-10 h-10"
          aria-label="القائمة">
          <Icon name="Menu" size={24} color="#C5A059" />
        </button>
      </div>
      {/* Mobile Drawer Overlay */}
      {isMobileDrawerOpen &&
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setIsMobileDrawerOpen(false)}>
          <div
            className="absolute top-0 right-0 bottom-0 w-80 bg-[#F9F7F2] shadow-2xl overflow-y-auto scrollbar-hide animate-slideInRight"
            onClick={(e) => e?.stopPropagation()}>
            <div className="flex justify-end p-4">
              <button
                onClick={() => setIsMobileDrawerOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5"
                aria-label="إغلاق">
                <Icon name="X" size={24} color="#5A5A5A" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4 text-center">
              <a href="/" className="block text-xl font-medium text-[#2C2B29] py-3" style={{ fontFamily: 'Amiri, serif' }}>الرئيسية</a>
              <a href="/about-dawaaer" className="block text-xl font-medium text-[#2C2B29] py-3" style={{ fontFamily: 'Amiri, serif' }}>عن دوائر</a>
              <a href="/about" className="block text-xl font-medium text-[#2C2B29] py-3" style={{ fontFamily: 'Amiri, serif' }}>من نحن؟</a>
              <a href="/policy" className="block text-xl font-medium text-[#2C2B29] py-3" style={{ fontFamily: 'Amiri, serif' }}>سياستنا</a>
            </div>

            <div className="mx-6 my-4 border-t border-[#C5A059]/30" />

            <div className="px-6 py-4">
              <h3 className="text-lg font-bold mb-4 text-right" style={{ fontFamily: 'Aref Ruqaa, serif', color: '#2C2B29' }}>
                دوائر
              </h3>
              <div className="space-y-2">
                {circles?.map((circle) =>
                  <button
                    key={circle?.id}
                    onClick={(e) => {
                      handleFilterChange(circle?.id, e);
                      setIsMobileDrawerOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all min-h-[44px] ${activeCircle === circle?.id ?
                      'bg-[#C5A059]/15 text-[#C5A059]' :
                      'text-[#2C2B29] hover:bg-[#C5A059]/10'}`
                    }
                    style={{ fontFamily: 'Amiri, serif' }}>
                    <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
                      {circle?.id === 'sufism' ?
                        <img
                          src="/assets/images/Screenshot_2026-01-22_223436-removebg-preview-1769116472162.png"
                          alt="دائرة الدائرة"
                          className="w-5 h-5 object-contain opacity-90"
                          style={{
                            filter: activeCircle === circle?.id ?
                              'grayscale(0%) contrast(125%)' :
                              'grayscale(100%) contrast(125%)'
                          }} /> :
                        circle?.id === 'soul' ?
                          <img
                            src="/assets/images/Screenshot_2026-01-22_223930-removebg-preview_1_-1769115911481.png"
                            alt="دائرة النفس"
                            className="w-7 h-7 object-contain opacity-100"
                            style={{
                              filter: activeCircle === circle?.id ?
                                'grayscale(0%) contrast(125%)' :
                                'grayscale(100%) contrast(125%)'
                            }} /> :
                          circle?.id === 'sound' ?
                            <img
                              src="/assets/images/vecteezy_black-sound-wave-icon-with-simple-and-modern-design_47445215-1769116873745.png"
                              alt="دائرة الصوت"
                              className="w-10 h-10 object-contain opacity-100"
                              style={{
                                filter: activeCircle === circle?.id ?
                                  'grayscale(0%) contrast(125%)' :
                                  'grayscale(100%) contrast(125%)'
                              }} /> :
                            circle?.id === 'reflections' ?
                              <img
                                src="/assets/images/Screenshot_2026-01-22_232822-removebg-preview-1769117314572.png"
                                alt="دائرة الرحلة"
                                className="w-6 h-6 object-contain opacity-90"
                                style={{
                                  filter: activeCircle === circle?.id ?
                                    'grayscale(0%) contrast(125%)' :
                                    'grayscale(100%) contrast(125%)'
                                }} /> :
                              circle?.id === 'language' ?
                                <img
                                  src="/assets/images/Screenshot_2026-01-22_233414-removebg-preview-1769117612785.png"
                                  alt="دائرة اللسان"
                                  className="w-7 h-7 object-contain opacity-100"
                                  style={{
                                    filter: activeCircle === circle?.id ?
                                      'grayscale(0%) contrast(125%)' :
                                      'grayscale(100%) contrast(125%)'
                                  }} /> :
                                circle?.id === 'heritage' ?
                                  <img
                                    src="/assets/images/Screenshot_2026-01-23_003258-removebg-preview-1769121221939.png"
                                    alt="دائرة التراث"
                                    className="w-9 h-9 object-contain opacity-100"
                                    style={{
                                      filter: activeCircle === circle?.id ?
                                        'grayscale(0%) contrast(125%)' :
                                        'grayscale(100%) contrast(125%)'
                                    }} /> :
                                  circle?.id === 'arts' ?
                                    <img
                                      src="/assets/images/images-removebg-preview-1769118454586.png"
                                      alt="دائرة الفنون"
                                      className="w-7 h-7 object-contain opacity-100"
                                      style={{
                                        filter: activeCircle === circle?.id ?
                                          'grayscale(0%) contrast(125%)' :
                                          'grayscale(100%) contrast(125%)'
                                      }} /> :

                                    <Icon
                                      name={circle?.icon}
                                      size={20}
                                      color={activeCircle === circle?.id ? '#C5A059' : circle?.color}
                                      style={{
                                        filter: activeCircle === circle?.id ?
                                          'grayscale(0%) contrast(125%) opacity(0.9)' :
                                          'grayscale(100%) contrast(125%) opacity(0.9)'
                                      }} />
                      }
                    </div>
                    <span className="flex-1 text-right text-base">{circle?.name}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Discussion Circles Button */}
            <div className="px-6 py-4">
              <button
                onClick={() => {
                  navigate('/discussions-feed');
                  setIsMobileDrawerOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all min-h-[44px] bg-[#C5A059]/10 text-[#C5A059] hover:bg-[#C5A059]/20"
                style={{ fontFamily: 'Amiri, serif' }}>
                <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
                  <Icon name="Users" size={20} color="#C5A059" />
                </div>
                <span className="flex-1 text-right text-base">حلقات نقاشية</span>
              </button>
            </div>

            <div className="px-6 py-6 mt-auto">
              <button
                onClick={() => navigate('/creator-studio')}
                className="w-full bg-[#C5A059] text-white py-4 rounded-full font-medium text-lg min-h-[44px]"
                style={{ fontFamily: 'Amiri, serif' }}>
                اترك أثراً
              </button>
            </div>
          </div>
        </div>
      }
      <main className="pt-16 lg:pt-20">
        <section className="py-12 md:py-16 lg:py-20">
          <div className="max-w-2xl mx-auto px-4 md:px-6 lg:px-8">
            <button
              onClick={handleBackClick}
              className="flex items-center gap-2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] mb-8 transition-colors">

              <Icon name="ArrowRight" size={20} />
              <span>عودة</span>
            </button>

            <div className="bg-[var(--color-card)] rounded-2xl p-8 md:p-10 shadow-warm">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-[var(--color-foreground)] mb-2">
                  {isLoginMode ? 'تسجيل الدخول' :
                    step === 2 ? 'اختر الدوائر التي تهتم بها' :
                      'كُن حلقةً في السلسلة'}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">

                {errors?.form && (
                  <div className="space-y-2">
                    <div className="bg-red-50 text-red-500 p-4 rounded-lg text-center text-sm">
                      {errors.form}
                    </div>
                    {errors?.requiresVerification && errors?.email && (
                      <button
                        type="button"
                        onClick={() => navigate(`/verify-email?email=${encodeURIComponent(errors.email)}`)}
                        className="w-full text-[var(--color-primary)] hover:underline text-sm"
                      >
                        إعادة إرسال رابط التحقق
                      </button>
                    )}
                  </div>
                )}

                {/* Login Form */}
                {isLoginMode && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2 text-right">
                        البريد الإلكتروني *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData?.email}
                        onChange={handleInputChange}
                        placeholder="أدخِل بريدك الإلكتروني"
                        className={errors?.email ? 'border-red-500' : ''}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2 text-right">
                        كلمة المرور *
                      </label>
                      <Input
                        type="password"
                        name="password"
                        value={formData?.password}
                        onChange={handleInputChange}
                        placeholder="أدخِل كلمة المرور"
                        className={errors?.password ? 'border-red-500' : ''}
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="default"
                      size="lg"
                      disabled={loading}
                      className="w-full rounded-full py-4 px-8 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      {loading ? 'جاري التحميل...' : 'تسجيل الدخول'}
                    </Button>
                  </>
                )}

                {/* Signup Forms */}
                {!isLoginMode && step === 1 && (
                  <>
                    {/* Profile Picture */}
                    <div className="flex flex-col items-center mb-6">
                      <label className="block text-sm font-medium text-[var(--color-foreground)] mb-3 text-center">
                        الصورة الشخصية (اختياري)
                      </label>
                      <div className="relative">
                        <div className="w-32 h-32 rounded-full overflow-hidden bg-[var(--color-background)] border-4 border-[var(--color-border)] shadow-[0_8px_24px_rgba(139,105,20,0.08)]">
                          {previewImage ? (
                            <img src={previewImage} alt="معاينة الصورة" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Icon name="User" size={48} color="var(--color-muted-foreground)" />
                            </div>
                          )}
                        </div>
                        <label className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-[var(--color-accent)] flex items-center justify-center cursor-pointer shadow-warm hover:bg-[var(--color-primary)] transition-colors">
                          <Icon name="Camera" size={20} color="white" />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>

                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2 text-right">
                        الاسم *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData?.name}
                        onChange={handleInputChange}
                        placeholder="أدخِل اسمك"
                        className={errors?.name ? 'border-red-500' : ''}
                      />
                      {errors?.name && (
                        <p className="text-red-500 text-xs mt-1 text-right">{errors?.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2 text-right">
                        البريد الإلكتروني *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData?.email}
                        onChange={handleInputChange}
                        placeholder="أدخِل بريدك الإلكتروني"
                        className={errors?.email ? 'border-red-500' : ''}
                      />
                      {errors?.email && (
                        <p className="text-red-500 text-xs mt-1 text-right">{errors?.email}</p>
                      )}
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2 text-right">
                        كلمة المرور *
                      </label>
                      <Input
                        type="password"
                        name="password"
                        value={formData?.password}
                        onChange={handleInputChange}
                        placeholder="أدخِل كلمة المرور"
                        className={errors?.password ? 'border-red-500' : ''}
                      />
                      {errors?.password && (
                        <p className="text-red-500 text-xs mt-1 text-right">{errors?.password}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      variant="default"
                      size="lg"
                      disabled={loading}
                      className="w-full rounded-full py-4 px-8 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                      iconName="ArrowLeft"
                      iconPosition="right"
                    >
                      {loading ? 'جاري التحقق...' : 'متابعة'}
                    </Button>
                  </>
                )}

                {/* Step 2: Interests Selection */}
                {!isLoginMode && step === 2 && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-foreground)] mb-3 text-right">
                        اختر الدوائر التي تهتم بها *
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {circles?.map((circle) => (
                          <button
                            key={circle?.id}
                            type="button"
                            onClick={() => handleCircleToggle(circle?.id)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-300 ${formData?.selectedCircles?.includes(circle?.id)
                              ? 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)] shadow-warm'
                              : 'bg-[var(--color-muted)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/80'
                              }`}
                          >
                            <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
                              {circle?.id === 'sufism' ? (
                                <img
                                  src="/assets/images/Screenshot_2026-01-22_223436-removebg-preview-1769116472162.png"
                                  alt="دائرة الدائرة"
                                  className="w-5 h-5 object-contain opacity-90"
                                  style={{
                                    filter: 'grayscale(100%) contrast(125%)'
                                  }}
                                />
                              ) : circle?.id === 'soul' ? (
                                <img
                                  src="/assets/images/Screenshot_2026-01-22_223930-removebg-preview_1_-1769115911481.png"
                                  alt="دائرة النفس"
                                  className="w-7 h-7 object-contain opacity-100"
                                  style={{
                                    filter: 'grayscale(100%) contrast(125%)'
                                  }}
                                />
                              ) : circle?.id === 'sound' ? (
                                <img
                                  src="/assets/images/vecteezy_black-sound-wave-icon-with-simple-and-modern-design_47445215-1769116873745.png"
                                  alt="دائرة الصوت"
                                  className="w-10 h-10 object-contain opacity-100"
                                  style={{
                                    filter: 'grayscale(100%) contrast(125%)'
                                  }}
                                />
                              ) : circle?.id === 'language' ? (
                                <img
                                  src="/assets/images/Screenshot_2026-01-22_233414-removebg-preview-1769117612785.png"
                                  alt="دائرة اللسان"
                                  className="w-6 h-6 object-contain opacity-90"
                                  style={{
                                    filter: 'grayscale(100%) contrast(125%)'
                                  }}
                                />
                              ) : circle?.id === 'heritage' ? (
                                <img
                                  src="/assets/images/Screenshot_2026-01-23_003258-removebg-preview-1769121221939.png"
                                  alt="دائرة التراث"
                                  className="w-9 h-9 object-contain opacity-100"
                                  style={{
                                    filter: 'grayscale(100%) contrast(125%)'
                                  }}
                                />
                              ) : circle?.id === 'arts' ? (
                                <img
                                  src="/assets/images/images-removebg-preview-1769118454586.png"
                                  alt="دائرة الفنون"
                                  className="w-7 h-7 object-contain opacity-100"
                                  style={{
                                    filter: 'grayscale(100%) contrast(125%)'
                                  }}
                                />
                              ) : circle?.id === 'philosophy' ? (
                                <Icon name={circle?.icon} size={20} color="currentColor" style={{ opacity: 0.9 }} />
                              ) : circle?.id === 'visuals' ? (
                                <Icon name={circle?.icon} size={24} color="currentColor" style={{ opacity: 0.9 }} />
                              ) : (
                                <Icon name={circle?.icon} size={24} color="currentColor" style={{ opacity: 0.9 }} />
                              )}
                            </div>
                            <span className="text-sm font-medium">{circle?.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      variant="default"
                      size="lg"
                      className="w-full rounded-full py-4 px-8 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                      iconName="Check"
                      iconPosition="right"
                    >
                      إنشاء الحساب
                    </Button>
                  </>
                )}

                {/* Login/Signup Toggle */}
                <div className="mt-8 text-center border-t border-[var(--color-border)] pt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginMode(!isLoginMode);
                      setErrors({});
                      if (!isLoginMode) setStep(1);
                    }}
                    className="text-[var(--color-primary)] hover:underline font-medium"
                  >
                    {isLoginMode
                      ? 'ليس لديك حساب؟ أنشئ حساباً جديداً'
                      : 'لديك حساب بالفعل؟ سجل الدخول'}
                  </button>
                </div>

              </form>
            </div>
          </div>
        </section>
      </main>
    </div>
  );

};

export default Signup;